// =============================================================================
// features/documents/components/DocumentUploadDialog.tsx — Fluxo de upload de
// 3 passos (F46-S08, planejamento-documentos-anexos.md §4).
//
// 1. POST /documents/signed-url  2. PUT direto no storage (XHR, progresso)
// 3. POST /documents — tudo orquestrado por useDocumentUpload (hooks.ts).
//
// Regras: tipo de documento obrigatório ANTES do upload começar (dropzone
// desabilitada até selecionar); drag&drop + seleção múltipla; downscale
// acontece dentro de runDocumentUploadStep (o componente não sabe disso);
// falha em um arquivo não bloqueia os demais nem deixa card fantasma — cada
// item tem estado independente e só vira card real após o POST confirmar.
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { cn } from '../../../lib/cn';
import {
  parseLinkValue,
  shouldShowLinkBlock,
  useDocumentTypesCatalog,
  useDocumentUpload,
  useLeadLinkOptions,
  type UploadItem,
} from '../hooks';

import { formatFileSize } from './DocumentCard';

export interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  leadId: string;
  scope: 'lead' | 'analysis' | 'contract';
  contextId?: string | undefined;
}

// ─── Item de progresso ───────────────────────────────────────────────────────

function UploadItemRow({
  item,
  onRemove,
}: {
  item: UploadItem;
  onRemove: () => void;
}): React.JSX.Element {
  return (
    <li
      className="flex items-center gap-3 p-3 rounded-sm"
      style={{ background: 'var(--bg-inset)', border: '1px solid var(--border-subtle)' }}
    >
      <div className="flex-1 min-w-0">
        <p
          className="font-mono truncate"
          style={{ fontSize: 'var(--text-xs)', color: 'var(--text)' }}
        >
          {item.file.name}
        </p>
        <p className="font-sans" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>
          {formatFileSize(item.file.size)}
        </p>
        {item.status === 'uploading' && (
          <div
            className="mt-1.5 h-1.5 rounded-pill overflow-hidden"
            style={{ background: 'var(--surface-muted)' }}
            role="progressbar"
            aria-valuenow={item.progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-pill transition-[width] duration-200"
              style={{ width: `${item.progress}%`, background: 'var(--grad-azul)' }}
            />
          </div>
        )}
        {item.status === 'error' && (
          <p className="mt-1" style={{ fontSize: 'var(--text-xs)', color: 'var(--danger)' }}>
            {item.error}
          </p>
        )}
      </div>

      <span aria-hidden="true" className="shrink-0">
        {item.status === 'done' && (
          <span style={{ color: 'var(--success)' }}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        )}
        {item.status === 'pending' && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remover ${item.file.name}`}
            className={cn(
              'w-7 h-7 flex items-center justify-center rounded-sm text-ink-3',
              'hover:text-danger hover:bg-danger/10',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/20',
            )}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
            >
              <path d="M5 5l10 10M15 5l-10 10" />
            </svg>
          </button>
        )}
      </span>
    </li>
  );
}

// ─── Aviso de status do catálogo de tipos ───────────────────────────────────

function TypeNotice({
  tone,
  children,
}: {
  tone: 'danger' | 'warning' | 'success';
  children: React.ReactNode;
}) {
  return (
    <p
      className="rounded-sm px-3 py-2 font-sans"
      style={{
        fontSize: 'var(--text-sm)',
        color: `var(--${tone})`,
        background: `var(--${tone}-bg)`,
      }}
    >
      {children}
    </p>
  );
}

// ─── Dialog principal ────────────────────────────────────────────────────────

export function DocumentUploadDialog({
  open,
  onClose,
  leadId,
  scope,
  contextId,
}: DocumentUploadDialogProps): React.JSX.Element | null {
  const { data: types, isLoading: typesLoading, isError: typesError } = useDocumentTypesCatalog();

  // Vínculo opcional a análise/contrato — só faz sentido quando o upload
  // parte da ficha do lead (scope='lead'). Nas telas de análise/contrato o
  // vínculo já vem do contexto (contextId), sem escolha (F46-S08).
  const canPickLink = scope === 'lead';
  const linkOptionsQuery = useLeadLinkOptions(leadId, canPickLink && open);
  const [linkValue, setLinkValue] = React.useState('');
  const parsedLink = parseLinkValue(linkValue);

  const { items, addFiles, removeItem, reset, startUpload, isUploading, isAllDone } =
    useDocumentUpload({
      leadId,
      scope: parsedLink?.scope ?? scope,
      contextId: parsedLink?.contextId ?? contextId,
    });

  const [documentTypeId, setDocumentTypeId] = React.useState('');
  const [isDragging, setIsDragging] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!open) {
      reset();
      setDocumentTypeId('');
      setLinkValue('');
    }
  }, [open]);

  if (!open) return null;

  const hasTypes = (types?.length ?? 0) > 0;
  const canStart = documentTypeId !== '' && items.length > 0 && !isUploading;
  const noTypeSelected = documentTypeId === '';

  const handleFiles = (fileList: FileList | null): void => {
    if (fileList) addFiles(Array.from(fileList));
  };
  const handleClose = (): void => {
    if (!isUploading) onClose();
  };

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/30 backdrop-blur-[2px]"
        onClick={handleClose}
        style={{ animation: 'fade-in 200ms ease both' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="document-upload-title"
        className="fixed left-1/2 top-1/2 z-[160] w-full max-w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-md flex flex-col max-h-[85vh]"
        style={{
          background: 'var(--bg-elev-1)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--elev-5)',
        }}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle shrink-0">
          <h2
            id="document-upload-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            Anexar documento
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isUploading}
            aria-label="Fechar"
            className={cn(
              'w-8 h-8 flex items-center justify-center rounded-sm text-ink-3',
              'hover:text-ink hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed',
              'focus-visible:ring-2 focus-visible:ring-azul/20 focus-visible:outline-none',
            )}
          >
            <svg
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-5 h-5"
            >
              <path d="M5 5l10 10M15 5l-10 10" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {typesError ? (
            <TypeNotice tone="danger">Não foi possível carregar os tipos de documento.</TypeNotice>
          ) : !typesLoading && !hasTypes ? (
            <TypeNotice tone="warning">Nenhum tipo de documento cadastrado no catálogo.</TypeNotice>
          ) : (
            <Select
              id="document-upload-type"
              label="Tipo de documento"
              required
              placeholder={typesLoading ? 'Carregando…' : 'Selecione o tipo'}
              disabled={typesLoading || isUploading}
              value={documentTypeId}
              onChange={(e) => setDocumentTypeId(e.target.value)}
              options={(types ?? []).map((t) => ({ value: t.id, label: t.label }))}
              hint="Obrigatório antes de iniciar o upload."
            />
          )}

          {canPickLink &&
            shouldShowLinkBlock({
              loading: linkOptionsQuery.isLoading,
              optionsCount: linkOptionsQuery.options.length,
              forbidden: linkOptionsQuery.isForbidden,
            }) && (
              <div className="flex flex-col gap-2">
                {linkOptionsQuery.options.length > 0 && (
                  <Select
                    id="document-upload-link"
                    label="Vincular a (opcional)"
                    disabled={isUploading}
                    value={linkValue}
                    onChange={(e) => setLinkValue(e.target.value)}
                    options={[
                      { value: '', label: 'Nenhum vínculo — fica só na ficha do lead' },
                      ...linkOptionsQuery.options,
                    ]}
                    hint="Só análises e contratos deste cidadão aparecem aqui."
                  />
                )}
                {linkOptionsQuery.isForbidden && (
                  <p
                    className="font-sans"
                    style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}
                  >
                    Algumas análises ou contratos deste lead podem não estar visíveis para o seu
                    perfil de acesso.
                  </p>
                )}
              </div>
            )}

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={noTypeSelected ? -1 : 0}
            aria-disabled={noTypeSelected}
            className={cn(
              'rounded-md p-6 flex flex-col items-center gap-2 text-center cursor-pointer',
              'border-2 border-dashed transition-all duration-fast',
              isDragging ? 'border-azul bg-azul/5' : 'border-border-strong hover:border-azul/60',
              noTypeSelected && 'opacity-50 pointer-events-none',
            )}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              style={{ color: 'var(--text-3)' }}
              aria-hidden="true"
            >
              <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
              <path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
            </svg>
            <p
              className="font-sans font-semibold"
              style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}
            >
              Arraste arquivos ou clique para selecionar
            </p>
            <p className="font-sans" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)' }}>
              PDF, JPEG, PNG ou WebP
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/webp"
              className="hidden"
              disabled={noTypeSelected}
              onChange={(e) => {
                handleFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {items.length > 0 && (
            <ul className="flex flex-col gap-2">
              {items.map((item) => (
                <UploadItemRow key={item.id} item={item} onRemove={() => removeItem(item.id)} />
              ))}
            </ul>
          )}

          {isAllDone && <TypeNotice tone="success">Todos os arquivos foram enviados.</TypeNotice>}
        </div>

        <div className="flex gap-3 px-6 py-5 border-t border-border-subtle shrink-0">
          <Button
            type="button"
            variant="ghost"
            onClick={handleClose}
            disabled={isUploading}
            className="flex-1"
          >
            {isAllDone ? 'Fechar' : 'Cancelar'}
          </Button>
          {!isAllDone && (
            <Button
              type="button"
              variant="primary"
              disabled={!canStart}
              onClick={() => void startUpload(documentTypeId)}
              className="flex-1"
            >
              {isUploading ? 'Enviando…' : 'Enviar'}
            </Button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
